import { useEffect, useState } from "react";
import { useGetAdminUsersQuery } from "@/api/api";
import { Button } from "../button";
import { Input } from "../input";
import { Skeleton } from "../skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import AdminUserRow from "./AdminUserRow";
import AdminUserCreateDialog from "./AdminUserCreateDialog";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export default function AdminUserTable({
  onError,
}: {
  onError: (message: string | null) => void;
}) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(input.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, isLoading, isFetching } = useGetAdminUsersQuery({
    q: q || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search by id, nickname, username, name or email"
          className="max-w-sm"
        />
        <AdminUserCreateDialog onError={onError} />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Username</TableHead>
            <TableHead>Nickname</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Exempt</TableHead>
            <TableHead>Enabled</TableHead>
            <TableHead>Slots</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={8}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : data && data.items.length > 0 ? (
            data.items.map((user) => (
              <AdminUserRow key={user.id} user={user} onError={onError} />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground">
                No users found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= data.total || isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
